import { Badge, Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '@prooffleet/client';
import { severityColor, verdictColor } from '@prooffleet/ui';

interface Row {
  _id: string;
  unitNumber: string;
  verdict: string;
  counts: { critical: number; warn: number };
  submittedAt: { serverAt: string };
}

interface DefectRow {
  _id: string;
  label: string;
  status: string;
  severity: string;
  unitNumber?: string;
}

/**
 * История водителя: что он сдал и что стало с его замечаниями.
 *
 * У роли `driver` есть права `inspection:read` и `defect:read` с областью
 * `own` — то есть «свои осмотры, свои дефекты». Экрана под них не было, и
 * заявленное водителем исчезало из его поля зрения навсегда.
 *
 * Это не удобство, а условие доверия: человек, чьи замечания уходят в пустоту,
 * через месяц начинает ставить галочки не глядя — ровно то, от чего продукт
 * и уходит.
 */
export function History(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['my-inspections'],
    queryFn: () => api<{ inspections: Row[] }>('/inspections?limit=50'),
  });

  const { data: units } = useQuery({
    queryKey: ['units'],
    queryFn: () => api<{ units: { _id: string; unitNumber: string }[] }>('/units'),
  });

  // Дефекты собираются по машинам площадки: отдельного «мои дефекты» в API нет,
  // а область `own` и так отсечёт чужие на сервере.
  const { data: defects } = useQuery({
    queryKey: ['my-defects', units?.units.map((u) => u._id)],
    enabled: (units?.units.length ?? 0) > 0,
    queryFn: async () => {
      const all = await Promise.all(
        (units?.units ?? []).map(async (unit) => {
          const res = await api<{ defects: DefectRow[] }>(`/units/${unit._id}/open-defects`);
          return res.defects.map((d) => ({ ...d, unitNumber: unit.unitNumber }));
        }),
      );
      return all.flat();
    },
  });

  const rows = data?.inspections ?? [];
  const open = defects ?? [];

  return (
    <div className="pf-screen">
      <div className="pf-scroll">
        <Stack gap="lg" maw={640} mx="auto" w="100%">
          <Title order={1}>{t('history.title')}</Title>

          {open.length > 0 && (
            <Stack gap="xs">
              <Text fw={600}>{t('history.openDefects')}</Text>
              {open.map((defect) => (
                <Card key={defect._id}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <div>
                      <Text fw={600}>{defect.unitNumber}</Text>
                      <Text>{defect.label}</Text>
                    </div>
                    {/* Цвет несёт тяжесть, текст — стадию: смешивать их значит
                        красить «важный» в нейтральный, а `verdictColor` про
                        исход осмотра, а не про дефект. */}
                    <Stack gap={4} align="flex-end">
                      <Badge color={severityColor(defect.severity)}>{defect.severity}</Badge>
                      <Text fz="xs" c="dimmed">
                        {defect.status}
                      </Text>
                    </Stack>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}

          <Stack gap="xs">
            <Text fw={600}>{t('history.inspections')}</Text>

            {isLoading && <Text c="dimmed">{t('common.loading')}</Text>}

            {!isLoading && rows.length === 0 && (
              <Card>
                <Text c="dimmed">{t('history.empty')}</Text>
              </Card>
            )}

            {rows.map((row) => (
              <Card key={row._id}>
                <Group justify="space-between" align="center" wrap="nowrap">
                  <div>
                    <Text fw={600}>{row.unitNumber}</Text>
                    <Text c="dimmed" fz="sm">
                      {new Date(row.submittedAt.serverAt).toLocaleString()}
                    </Text>
                  </div>
                  <Badge color={verdictColor(row.verdict)} size="lg">
                    {row.counts.critical > 0
                      ? t('history.critical', { count: row.counts.critical })
                      : row.counts.warn > 0
                        ? t('history.findings', { count: row.counts.warn })
                        : t('history.clean')}
                  </Badge>
                </Group>
              </Card>
            ))}
          </Stack>
        </Stack>
      </div>

      <div className="pf-action-bar">
        <Button fullWidth onClick={() => void navigate('/units')}>
          {t('history.back')}
        </Button>
      </div>
    </div>
  );
}
